import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { startCheckout } from "@/pages/PaymentPages";
import { ScoreRing, ShareBadge } from "@/components/ScoreBadge";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/lib/i18n";
import { AlertOctagon, AlertTriangle, Download, Sparkles, ExternalLink, ArrowRight, Loader2 } from "lucide-react";
import AnnotatedImage from "@/components/AnnotatedImage";
import ScanHistory from "@/components/ScanHistory";
import { VaultVerifyButton, VaultVerificationPanel } from "@/components/VaultVerification";
import AuthorityReportDialog from "@/components/AuthorityReportDialog";

const VERDICT_STYLE = {
  non_conforme: "tag-red",
  a_qualifier: "tag-orange",
  acceptable_avec_preuve: "tag-green",
  hors_contexte: "tag-green",
};

export default function FullScanPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [url, setUrl] = useState("");
  const [scan, setScan] = useState(null);
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();
  const { t } = useLang();
  const pollTimer = useRef(null);
  const [elapsed, setElapsed] = useState(0);
  const [pollStopped, setPollStopped] = useState(false);

  useEffect(() => {
    if (!id) return;
    let stopped = false;
    const startedAt = Date.now();
    const fetchOnce = async () => {
      try {
        const r = await api.get(`/scan/full/${id}`);
        if (stopped) return;
        setScan(r.data);
        const mins = (Date.now() - startedAt) / 60000;
        setElapsed(mins);
        if (r.data?.status === "processing") {
          // Garde-fou : on arrête d'interroger le serveur au bout de 15 min pour ne pas
          // laisser un spinner tourner indéfiniment. Le watchdog serveur reprend ou
          // marque le scan interrompu, et l'utilisateur reçoit un e-mail.
          if (mins >= 15) { setPollStopped(true); return; }
          pollTimer.current = setTimeout(fetchOnce, 4000);
        } else if (r.data?.status === "failed" || r.data?.status === "interrupted") {
          toast.error(r.data?.error || "Scan interrompu : erreur inconnue");
        }
      } catch { if (!stopped) toast.error("Introuvable"); }
    };
    fetchOnce();
    return () => { stopped = true; if (pollTimer.current) clearTimeout(pollTimer.current); };
  }, [id]);

  const relaunch = async () => {
    if (!scan?.url) return;
    setLoading(true);
    try {
      const { data } = await api.post("/scan/full", { url: scan.url });
      toast.success("Scan relancé");
      nav(`/app/scan/${data.id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setLoading(false); }
  };

  const run = async () => {
    if (!url) return;
    setLoading(true);
    try {
      if (user?.plan === "surveillance") {
        const { data } = await api.post("/scan/full", { url });
        toast.success("Scan lancé — traitement en cours (jusqu'à 5 min)");
        nav(`/app/scan/${data.id}`);
      } else {
        toast.loading(t("pay.redirect"), { id: "s" });
        await startCheckout("scan_full", url);
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur", { id: "s" }); }
    finally { setLoading(false); }
  };

  const dl = async () => {
    const r = await api.get(`/scan/full/${id}/pdf`, { responseType: "blob" });
    const link = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
    const a = document.createElement("a"); a.href = link; a.download = `empco-scan.pdf`; a.click();
  };

  if (!id) {
    return (
      <div className="space-y-6" data-testid="full-scan-form">
        <div>
          <p className="eyebrow mb-1">Scan site internet</p>
          <h1 className="text-3xl sm:text-4xl font-serif tracking-tight">Lancer un scan site internet (≤200 pages)</h1>
        </div>
        <div className="hairline p-6 rounded-lg flex gap-3">
          <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://exemple.fr" className="rounded-lg h-12 flex-1 border-border" data-testid="full-scan-url" />
          <Button onClick={run} disabled={loading || !url} className="rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 h-12" data-testid="full-scan-submit">
            {loading ? "…" : "Scanner"} <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
        <p className="font-mono text-xs text-muted-foreground">{user?.plan === "surveillance" ? t("fullscan.included") : t("fullscan.paid")}</p>
        <ScanHistory source="web" title="Historique — Scans site internet" />
      </div>
    );
  }

  if (!scan) return <p className="font-mono text-sm text-muted-foreground">Chargement…</p>;

  if (scan.status === "processing") {
    return (
      <div className="space-y-6" data-testid="full-scan-processing">
        <div>
          <p className="eyebrow mb-1">Scan en cours</p>
          <h1 className="text-3xl sm:text-4xl font-serif tracking-tight break-all">{scan.url}</h1>
        </div>
        <div className="hairline p-10 rounded-lg text-center space-y-4">
          <Loader2 className="w-10 h-10 mx-auto animate-spin text-primary" />
          <p className="font-serif text-lg">Traitement en cours…</p>
          {scan.pages_scanned > 0 && (
            <p className="font-mono text-sm text-primary" data-testid="scan-pages-progress">
              {scan.pages_scanned} page{scan.pages_scanned > 1 ? "s" : ""} crawlée{scan.pages_scanned > 1 ? "s" : ""}
            </p>
          )}
          <p className="text-sm text-muted-foreground max-w-md mx-auto">
            Crawl + qualification IA — peut prendre jusqu'à 5 min sur un gros site.
            Cette page se rafraîchit toute seule. Vous pouvez la fermer et revenir plus tard :
            vous recevrez un e-mail dès que le rapport est prêt.
          </p>
          {elapsed >= 6 && !pollStopped && (
            <p className="text-sm text-[hsl(29_87%_40%)] max-w-md mx-auto" data-testid="scan-slow-notice">
              C'est plus long que prévu sur ce site. Le traitement continue côté serveur :
              vous pouvez fermer cette page, l'e-mail de fin de scan vous prévient.
            </p>
          )}
          {pollStopped && (
            <div className="space-y-3" data-testid="scan-poll-stopped">
              <p className="text-sm text-muted-foreground max-w-md mx-auto">
                Nous arrêtons le rafraîchissement automatique après 15 minutes. Le scan est toujours
                suivi côté serveur : il sera finalisé ou marqué interrompu, et vous serez averti par e-mail.
              </p>
              <div className="flex items-center justify-center gap-3">
                <Button onClick={() => window.location.reload()} variant="outline" className="rounded-lg" data-testid="scan-refresh-btn">
                  Actualiser
                </Button>
                <Button onClick={relaunch} disabled={loading} className="rounded-lg bg-primary text-primary-foreground" data-testid="scan-relaunch-btn">
                  Relancer le scan
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (scan.status === "failed" || scan.status === "interrupted") {
    return (
      <div className="space-y-6" data-testid="full-scan-failed">
        <div>
          <p className="eyebrow mb-1 text-[hsl(var(--status-red))]">{scan.status === "interrupted" ? "Scan interrompu" : "Scan échoué"}</p>
          <h1 className="text-3xl sm:text-4xl font-serif tracking-tight break-all">{scan.url}</h1>
        </div>
        <div className="hairline p-10 rounded-lg text-center space-y-3">
          <AlertOctagon className="w-10 h-10 mx-auto text-[hsl(var(--status-red))]" />
          <p className="text-sm text-muted-foreground max-w-md mx-auto">{scan.error || "Une erreur est survenue pendant le traitement."}</p>
          <div className="flex items-center justify-center gap-3">
            <Button onClick={relaunch} disabled={loading || !scan.url} className="rounded-lg bg-primary text-primary-foreground" data-testid="scan-relaunch-failed-btn">
              Relancer ce scan
            </Button>
            <Button onClick={() => nav("/app/scan/full")} variant="outline" className="rounded-lg">Nouveau scan</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="full-scan-report">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <p className="eyebrow mb-1">{scan.source === "image" ? "Analyse image" : scan.source === "document" ? "Analyse document" : "Rapport complet"} · {new Date(scan.scanned_at).toLocaleString()} · <span className="text-primary">{scan.version_label || "v1"}</span></p>
          <h1 className="text-3xl font-serif tracking-tight break-all">{scan.source === "image" ? (scan.image_name || scan.url) : scan.source === "document" ? (scan.document_name || scan.url) : scan.url}</h1>
          <p className="font-mono text-xs text-muted-foreground mt-2">{scan.source === "image" ? `Image · ${scan.total_words} mots détectés` : scan.source === "document" ? `Document · ${scan.total_words} mots${scan.pages_scanned ? ` · ${scan.pages_scanned} pages` : ""}` : `${scan.pages_scanned} pages · ${scan.total_words} mots`}</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <VaultVerifyButton scanId={scan.id} onVerified={(v) => setScan(s => ({
            ...s, vault_verification: v, verified_score: v.score_after, verified_grade: v.grade_after,
            version_label: v.version_label, verification_history: v.verification_history,
          }))} />
          <AuthorityReportDialog />
          <Button onClick={dl} className="rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 h-11" data-testid="download-pdf-btn">
            <Download className="w-4 h-4 mr-2" /> PDF horodaté
          </Button>
        </div>
      </div>

      {scan.vault_verification && (
        <VaultVerificationPanel v={scan.vault_verification} history={scan.verification_history} />
      )}

      <div className="grid md:grid-cols-[auto_1fr] gap-4 items-stretch">
        <div className="hairline p-5 flex items-center gap-5" data-testid="full-score">
          <ScoreRing score={(scan.verified_score ?? scan.compliance_score) ?? 0} size={120} stroke={10} />
          <div>
            <p className="eyebrow mb-1">{t("score.title")}</p>
            {scan.verified_score != null && (
              <p className="font-mono text-[10px] text-[hsl(var(--status-green))] mb-1" data-testid="score-vault-verified">{t("vault.verified.badge")} · {scan.compliance_score} → {scan.verified_score}</p>
            )}
            <p className="text-sm text-muted-foreground max-w-[220px]">{t("score.hint")}</p>
          </div>
        </div>
        <ShareBadge scanId={scan.id} score={scan.compliance_score ?? 0} />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat label="Red flags" value={scan.red_count} Icon={AlertOctagon} accent="red" testid="stat-red" />
        <Stat label="Orange flags" value={scan.orange_count} Icon={AlertTriangle} accent="orange" testid="stat-orange" />
        <Stat label="Non conformes (LLM)" value={scan.non_compliant_count} testid="stat-nc" />
        <Stat label="Exposition €" value={(scan.financial_exposure_eur || 0).toLocaleString("fr-FR")} testid="stat-expo" />
      </div>

      {scan.source === "image" && scan.image_b64 && (
        <AnnotatedImage imageB64={scan.image_b64} mime={scan.image_mime} regions={scan.annotated_regions} alt={scan.image_name} />
      )}

      <div className="hairline">
        {(scan.claims || []).map((c, i) => (
          <div key={c.id} className={`p-5 ${i > 0 ? "border-t border-border" : ""}`} data-testid={`full-claim-${c.id}`}>
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <span className={`px-1.5 h-5 text-[10px] font-mono flex items-center rounded-lg ${c.severity === "red" ? "tag-red" : "tag-orange"}`}>{c.severity.toUpperCase()}</span>
              <span className={`px-2 py-0.5 text-[10px] font-mono flex items-center rounded-lg uppercase ${VERDICT_STYLE[c.llm_verdict] || "tag-orange"}`}>{(c.llm_verdict || "").replace(/_/g," ")}</span>
              <span className="font-mono text-[10px] text-muted-foreground">{c.category}</span>
            </div>
            <p className="font-serif text-base leading-snug">« {c.excerpt} »</p>
            {c.llm_rationale && <p className="text-xs text-muted-foreground mt-2 leading-relaxed">Motif · {c.llm_rationale}</p>}
            {c.llm_reformulation && <p className="text-sm text-[hsl(var(--status-green))] mt-2 flex items-start gap-2"><Sparkles className="w-3.5 h-3.5 mt-1 shrink-0" /> « {c.llm_reformulation} »</p>}
          </div>
        ))}
      </div>
    </div>
  );
}

function Stat({ label, value, Icon, accent, testid }) {
  return (
    <div className="hairline p-6" data-testid={testid}>
      <div className="flex items-center justify-between mb-4">
        <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{label}</p>
        {Icon && <Icon className={`w-4 h-4 ${accent === "red" ? "text-[hsl(var(--status-red))]" : accent === "orange" ? "text-[hsl(29_87%_50%)]" : "text-muted-foreground"}`} />}
      </div>
      <p className={`text-3xl sm:text-4xl font-serif tracking-tight ${accent === "red" ? "text-[hsl(var(--status-red))]" : accent === "orange" ? "text-[hsl(29_87%_50%)]" : ""}`}>{value}</p>
    </div>
  );
}
